import { Component, ChangeDetectionStrategy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { ToastrService } from 'ngx-toastr';
import { ActionTypeEnum } from 'src/app/audit-log/action-type.enum';
import { FirestoreRulesService } from 'src/app/core/firestore-rules/firestore-rules.service';
import { UserAdminVerificationStatusEnum } from 'src/app/users/interfaces/user-values';
import { UserService } from 'src/app/users/services/user.service';
@Component({
  selector: 'cell-actions-users',
  templateUrl: './cell-actions-users.component.html',
  styleUrls: ['./cell-actions-users.component.scss'],
})
export class CellActionsUsersComponent implements ICellRendererAngularComp, OnInit {
  refresh;
  isAdminMark: boolean;
  private params: any;
  constructor(private readonly translate: TranslateService,
              private readonly userService: UserService,
              private readonly toastr: ToastrService,
              private readonly firestoreRulesService: FirestoreRulesService) {}

  ngOnInit(): void {
    const isAdmin = this.firestoreRulesService.getisUserAdmin(this.params.data.metadata.docId)
    .then((isAdminRes) => {
      this.isAdminMark = isAdminRes as boolean;
      const data = this.params.node?.data;
      if (data) { data.isAdmin = isAdminRes; }
    })
    .catch((error) => {
      console.error(error);
    });
  }

  agInit(params: ICellRendererParams): void {
    this.params = params;
  }

  stopPropagation(event: MouseEvent): void {
    event.stopPropagation();
  }

  btnDisableClickedHandler = () => {
    // this.params.debugClickElement(this.params?.data?.metadata);
    this.updateUserStatus(UserAdminVerificationStatusEnum.BLOCKED);
  }

  btnApproveClickedHandler = () => {
    // this.params.debugClickElement(this.params?.data?.metadata);
    this.updateUserStatus(UserAdminVerificationStatusEnum.APPROVED);
  }

  btnMakeAdminClickedHandler = () => {
    // this.params.debugClickElement(this.params?.data?.metadata);
    this.makeAdmin();
  }

  btnRemoveAdminClickedHandler = () => {
    // this.params.debugClickElement(this.params?.data?.metadata);
    this.removeAdmin();
  }

  btnDeleteClickedHandler = () => {
    this.deleteUser();
  }

  updateUserStatus = (adminVerificationStatus: UserAdminVerificationStatusEnum): void => {
    const user = this.params?.data;
    const updatedUser = {
      ...user,
      adminVerificationStatus,
      metadata: { ...user.metadata, translated: { adminVerificationStatus: this.translate.instant(adminVerificationStatus) } },
    };
    const resourceId = this.params?.data?.metadata?.docId;

    const successMessage =
      adminVerificationStatus === UserAdminVerificationStatusEnum.APPROVED
        ? this.translate.instant('RESOURCE_LIST.USERS_TABLE.USER_APPROVED')
        : this.translate.instant('RESOURCE_LIST.USERS_TABLE.USER_BLOCKED');
    this.userService
      .updateUserStatus(resourceId, adminVerificationStatus)
      .then(() => {
        this.toastr.success(successMessage);
        this.params?.refresh({
          id: resourceId,
          action: ActionTypeEnum.UPDATED,
          value: updatedUser,
        });
      })
      .catch((error) => {
        console.error(error);
        this.toastr.error(this.translate.instant('RESOURCE_LIST.USERS_TABLE.FAILED'), 'Error!');
      });
  }

  makeAdmin = (): void => {
    try {
      this.firestoreRulesService.addUserToAdmin(this.params.data.metadata.docId);
      this.firestoreRulesService.removeUserFromRoles(this.params.data.metadata.docId);
      this.toastr.success(this.translate.instant('RESOURCE_LIST.USERS_TABLE.MAKE_ADMIN_SUCCESS'));
      const rowNode = this.params.api.getRowNode(this.params.data.email);
      this.params.api.refreshRows({ force: true, rowNodes: [rowNode]});
      this.params.api.flashCells({ rowNodes: [rowNode] });
    } catch (err) {
      console.error(err);
      this.toastr.error(this.translate.instant('RESOURCE_LIST.USERS_TABLE.MAKE_ADMIN_FAIL'));
    }
  }

  removeAdmin = (): void => {
    try {
      this.firestoreRulesService.addUserToRoles(this.params.data.metadata.docId);
      this.firestoreRulesService.removeUserFromAdmin(this.params.data.metadata.docId);
      this.toastr.success(this.translate.instant('RESOURCE_LIST.USERS_TABLE.MAKE_ADMIN_SUCCESS'));
      const rowNode = this.params.api.getRowNode(this.params.data.email);
      this.params.api.refreshCells({ force: true, rowNodes: [rowNode]});
      this.params.api.flashCells({ rowNodes: [rowNode] });

    } catch (err) {
      console.error(err);
      this.toastr.error(this.translate.instant('RESOURCE_LIST.USERS_TABLE.MAKE_ADMIN_FAIL'));
    }
  }

  deleteUser = (): void => {
    try {
      const uid = this.params.data.metadata.docId;
      const rowNode = this.params.api.getRowNode(this.params.data.email);
      this.userService.deleteUser(uid).then(() => {
        this.toastr.success(this.translate.instant('RESOURCE_LIST.USERS_TABLE.DELETE_USER_SUCCESS'));
        this.params.api.updateRowData({remove: [rowNode.data]});
        // this.params.api.applyTransaction({ remove: [rowNode] });
      }).catch((err) => {
        console.error(err);
        this.toastr.error(this.translate.instant('RESOURCE_LIST.USERS_TABLE.DELETE_USER_FAIL'));
      });

    } catch (err) {
      console.error(err);
      this.toastr.error(this.translate.instant('RESOURCE_LIST.USERS_TABLE.MAKE_ADMIN_FAIL'));
    }
  }

  isBlocked = (): boolean => {
    return this.params.data.adminVerificationStatus === UserAdminVerificationStatusEnum.BLOCKED;
  }

  isApproved = (): boolean => {
    return this.params.data.adminVerificationStatus === UserAdminVerificationStatusEnum.APPROVED;
  }

  isPending = (): boolean => {
    return this.params.data.adminVerificationStatus === UserAdminVerificationStatusEnum.PENDING;
  }
}
